import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LoginScreen.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function callEdgeFunction(name, body) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const EyeOpen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9.88 9.88a3 3 0 104.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61" />
    <line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);

const CheckCircle = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    stroke={active ? '#4ade80' : 'rgba(255,255,255,0.25)'}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const Sparkle = ({ style }) => (
  <svg style={style} width="18" height="18" viewBox="0 0 24 24" fill="#D8A134">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
  </svg>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [email, setEmail] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState('');

  // Step 2 — OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(45);
  const [canResend, setCanResend] = useState(false);
  const inputsRef = useRef([]);

  // Step 2 — new password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    if (step !== 2) return;
    if (countdown <= 0) { setCanResend(true); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, step]);

  const hasMin8 = newPassword.length >= 8;
  const hasNumberOrSpecial = /[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
  const hasMixedCase = /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setSendError('');
    if (!email.trim()) { setSendError('Please enter your email address'); return; }
    setSendLoading(true);
    try {
      const data = await callEdgeFunction('send-password-reset-otp', { email: email.trim() });
      if (data?.error === 'OAUTH_USER') {
        setSendError('This account uses Google sign-in. Please use "Sign in with Google" instead.');
        return;
      }
      if (!data?.success) {
        setSendError(data?.error || 'Failed to send code. Please try again.');
        return;
      }
      setStep(2);
      setCountdown(45);
      setCanResend(false);
    } catch (err) {
      setSendError('Failed to send code. Please check your connection and try again.');
    } finally {
      setSendLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const digit = value.slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setResetError('');
    if (digit && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (otp[index]) {
        const next = [...otp]; next[index] = ''; setOtp(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...otp];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleResend = async () => {
    if (!canResend) return;
    setCanResend(false);
    setCountdown(45);
    setOtp(['', '', '', '', '', '']);
    setResetError('');
    inputsRef.current[0]?.focus();
    try {
      const data = await callEdgeFunction('send-password-reset-otp', { email: email.trim() });
      if (!data?.success) {
        setResetError(data?.error || 'Failed to resend code. Please try again.');
      }
    } catch (err) {
      setResetError('Failed to resend code. Please check your connection and try again.');
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError('');
    const code = otp.join('');
    if (code.length < 6) { setResetError('Please enter all 6 digits of the code'); return; }
    if (!hasMin8 || !hasNumberOrSpecial || !hasMixedCase) { setResetError('Password does not meet all requirements'); return; }
    if (newPassword !== confirmPassword) { setResetError('Passwords do not match'); return; }
    setResetLoading(true);
    try {
      // Step 1: verify the OTP code
      const verifyData = await callEdgeFunction('verify-otp', {
        email: email.trim(),
        otp_code: code,
        purpose: 'password_reset',
      });
      if (!verifyData?.success) {
        const msg = verifyData?.error || 'Invalid or expired code. Please try again.';
        if (typeof verifyData?.attemptsRemaining === 'number') {
          setResetError(`${msg} (${verifyData.attemptsRemaining} attempt${verifyData.attemptsRemaining !== 1 ? 's' : ''} remaining)`);
        } else {
          setResetError(msg);
        }
        return;
      }
      // Step 2: OTP verified — update the password
      const resetData = await callEdgeFunction('update-user-password', {
        email: email.trim(),
        new_password: newPassword,
      });
      if (!resetData?.success) {
        setResetError(resetData?.error || 'Failed to reset password. Please try again.');
        return;
      }
      setStep(3);
    } catch (err) {
      setResetError('Something went wrong. Please check your connection and try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="login-screen">
      <div className="login-container" style={{ maxWidth: 440 }}>

        {/* ── STEP 1: Enter email ── */}
        {step === 1 && (
          <>
            <div className="login-header">
              <div className="login-logo-wrapper">
                <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
              </div>
              <h1 className="login-title">Reset Your Password</h1>
              <p className="login-subtitle">
                No worries! Enter your email address and we'll send you a 6-digit code to reset your password.
              </p>
            </div>

            <form className="login-form" onSubmit={handleSendCode}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <div className="fp-input-icon-wrapper">
                  <span className="fp-input-icon"><MailIcon /></span>
                  <input
                    type="email"
                    className="form-input fp-input-with-icon"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              {sendError && (
                <div className="login-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{sendError}</span>
                </div>
              )}

              <button type="submit" className="login-button" disabled={sendLoading}>
                {sendLoading ? 'Sending...' : 'Send Code'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button type="button" className="forgot-password-link" onClick={() => navigate('/standouts/login')}>
                ← Back to Sign In
              </button>
            </div>
          </>
        )}

        {/* ── STEP 2: Verify code + new password ── */}
        {step === 2 && (
          <>
            <div className="login-header" style={{ marginBottom: 24 }}>
              <div className="login-logo-wrapper">
                <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
              </div>
              <h1 className="login-title">Verify Code</h1>
              <p className="login-subtitle">Enter the 6-digit code we sent to</p>
              <p className="verify-email-highlight">{email}</p>
            </div>

            <form onSubmit={handleReset}>
              <div className="otp-boxes" style={{ marginBottom: 8 }}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (inputsRef.current[i] = el)}
                    className={`otp-box${digit ? ' otp-box-filled' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={handlePaste}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <p className="verify-resend-row" style={{ marginBottom: 20 }}>
                Didn't receive the code?{' '}
                <button
                  type="button"
                  className={`verify-resend-btn${canResend ? '' : ' verify-resend-disabled'}`}
                  onClick={handleResend}
                  disabled={!canResend}
                >
                  Resend
                </button>
                {!canResend && <span className="verify-countdown"> (0:{pad(countdown)})</span>}
              </p>

              <div className="fp-and-divider">
                <span className="fp-and-line" />
                <span className="fp-and-text">AND</span>
                <span className="fp-and-line" />
              </div>

              <div className="form-group" style={{ marginTop: 20 }}>
                <label className="form-label">New Password</label>
                <div className="fp-input-icon-wrapper">
                  <span className="fp-input-icon"><LockIcon /></span>
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="form-input fp-input-with-icon fp-input-with-icon-right"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowNew(v => !v)} tabIndex="-1">
                    {showNew ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <div className="fp-input-icon-wrapper">
                  <span className="fp-input-icon"><LockIcon /></span>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="form-input fp-input-with-icon fp-input-with-icon-right"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowConfirm(v => !v)} tabIndex="-1">
                    {showConfirm ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <div className="fp-requirements">
                <div className="fp-requirement-item">
                  <CheckCircle active={hasMin8} />
                  <span style={{ color: hasMin8 ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>At least 8 characters</span>
                </div>
                <div className="fp-requirement-item">
                  <CheckCircle active={hasNumberOrSpecial} />
                  <span style={{ color: hasNumberOrSpecial ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>Include a number or special character</span>
                </div>
                <div className="fp-requirement-item">
                  <CheckCircle active={hasMixedCase} />
                  <span style={{ color: hasMixedCase ? '#4ade80' : 'rgba(255,255,255,0.5)' }}>Mix of uppercase and lowercase letters</span>
                </div>
              </div>

              {resetError && (
                <div className="login-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{resetError}</span>
                </div>
              )}

              <button type="submit" className="login-button" disabled={resetLoading} style={{ marginTop: 8 }}>
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="forgot-password-link" onClick={() => setStep(1)}>
                ← Back
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3: Success ── */}
        {step === 3 && (
          <div style={{ textAlign: 'center' }}>
            <div className="login-logo-wrapper">
              <img src="/assets/images/logo1.png" alt="LegacyStat" className="login-logo" />
            </div>

            <img src="/assets/images/forgot.png" alt="" style={{ width: 200, height: 200, objectFit: 'contain', margin: '8px auto 0' }} />

            <h1 className="login-title" style={{ marginTop: 24 }}>Password Reset{'\n'}Successful!</h1>
            <p className="login-subtitle" style={{ marginTop: 8, marginBottom: 32 }}>
              Your password has been updated successfully.
            </p>

            <button className="login-button" onClick={() => navigate('/standouts/login')}>
              Continue to Sign In
            </button>

            <div style={{ marginTop: 16 }}>
              <button type="button" className="forgot-password-link" onClick={() => navigate('/standouts')}>
                Back to Home
              </button>
            </div>
          </div>
        )}

      </div>

      <div className="login-background-glow" />
      <div className="login-background-pattern" />
    </div>
  );
};

export default ForgotPassword;
